import { useState, useEffect } from 'react';
import { GRID_WIDTH, GRID_HEIGHT, GRID_SIZES, COLORS, DEFAULT_DIFFICULTY } from '../constants/gameConstants';
import { generateGrid } from '../utils/gridUtils';
import { ownFill, hasAbsorbableCells } from '../utils/gameLogic';
import { getAiMoveByDifficulty } from '../utils/aiLogic';
import { sortCellsByDistance } from '../utils/animationUtils';

// Helper: create grid, auto-absorb starting blobs, and compute initial scores/colors
const createInitialGameState = (gridWidth, gridHeight) => {
  const grid = generateGrid(gridWidth, gridHeight);

  // Start with only the two corner cells owned
  let ownership = Array.from({ length: gridHeight }, (_, row) =>
    Array.from({ length: gridWidth }, (_, col) =>
      row === 0 && col === 0
        ? 'Player'
        : row === gridHeight - 1 && col === gridWidth - 1
        ? 'AI'
        : null
    )
  );

  // Auto‑absorb the player's starting blob (same-color region from [0,0])
  let result = ownFill(
    grid,
    ownership,
    0,
    0,
    grid[0][0],
    grid[0][0], // same color: only expand ownership
    'Player',
    gridWidth,
    gridHeight
  );
  ownership = result.newOwnership;

  // Auto‑absorb the AI's starting blob (same-color region from bottom-right)
  result = ownFill(
    grid,
    ownership,
    gridHeight - 1,
    gridWidth - 1,
    grid[gridHeight - 1][gridWidth - 1],
    grid[gridHeight - 1][gridWidth - 1], // same color
    'AI',
    gridWidth,
    gridHeight
  );
  ownership = result.newOwnership;

  const playerScore = ownership.flat().filter((cell) => cell === 'Player').length;
  const aiScore = ownership.flat().filter((cell) => cell === 'AI').length;

  return {
    grid,
    ownership,
    playerScore,
    aiScore,
    playerColor: grid[0][0],
    aiColor: grid[gridHeight - 1][gridWidth - 1],
  };
};

export const useGameState = () => {
  const [gridWidth, setGridWidth] = useState(GRID_WIDTH);
  const [gridHeight, setGridHeight] = useState(GRID_HEIGHT);

  // Build the initial grid + ownership + scores with auto‑absorption
  const initialState = createInitialGameState(GRID_WIDTH, GRID_HEIGHT);

  const [grid, setGrid] = useState(initialState.grid);
  const [ownership, setOwnership] = useState(initialState.ownership);
  const [playerPosition] = useState([0, 0]);
  const [aiPosition, setAiPosition] = useState([GRID_HEIGHT - 1, GRID_WIDTH - 1]);
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [playerColor, setPlayerColor] = useState(initialState.playerColor);
  const [aiColor, setAiColor] = useState(initialState.aiColor);
  const [playerScore, setPlayerScore] = useState(initialState.playerScore);
  const [aiScore, setAiScore] = useState(initialState.aiScore);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [message, setMessage] = useState("");
  const [availableColors, setAvailableColors] = useState([]);
  const [animatingCells, setAnimatingCells] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState("Player");
  const [playerSkipped, setPlayerSkipped] = useState(false);
  const [aiSkipped, setAiSkipped] = useState(false);
  const [difficulty, setDifficulty] = useState(DEFAULT_DIFFICULTY);

  // Calculate available colors for the current player
  useEffect(() => {
    if (isPlayerTurn && !gameOver && !isAnimating) {
      const currentColor = playerColor;
      const opponentColor = aiColor;
      
      if (!currentColor || !opponentColor) return;
      
      const available = COLORS.filter(color => {
        // Don't allow selecting opponent's color
        if (color === opponentColor) {
          return false;
        }
        
        const tempGrid = grid.map(row => [...row]);
        const tempOwnership = ownership.map(row => [...row]);
        const {newOwnership} = ownFill(tempGrid, tempOwnership, playerPosition[0], playerPosition[1], currentColor, color, "Player", gridWidth, gridHeight);
        
        const beforeCount = ownership.flat().filter(cell => cell === "Player").length;
        const afterCount = newOwnership.flat().filter(cell => cell === "Player").length;
        
        return afterCount > beforeCount;
      });
      
      setAvailableColors(available);
      
      // Check if player has no moves on their turn
      if (available.length === 0 && !gameOver) {
        setPlayerSkipped(true);
        setMessage("Player has no valid moves. Turn skipped.");
        setIsPlayerTurn(false);
      }
    }
  }, [isPlayerTurn, grid, ownership, playerColor, aiColor, playerPosition, isAnimating, gridWidth, gridHeight]);

  // Check if the game is over
  const checkGameOver = () => {
    if (gameOver) return true;
    
    // Count owned tiles
    const totalOwnedTiles = ownership.flat().filter(cell => cell).length;
    const totalTiles = gridWidth * gridHeight;
    
    // Check if the board is completely filled
    if (totalOwnedTiles >= totalTiles) {
      endGame();
      return true;
    }
    
    // Check if both players have skipped a turn (neither can make a move)
    if (playerSkipped && aiSkipped) {
      endGame();
      return true;
    }
    
    return false;
  };
  
  // End game and determine winner
  const endGame = () => {
    const totalTiles = gridWidth * gridHeight;
    setGameOver(true);
    
    // Determine winner based on having more than 50% of the field
    if (playerScore > totalTiles * 0.5) {
      setWinner("Player");
      setMessage(`Game over! You win with ${playerScore} tiles (${Math.round(playerScore / totalTiles * 100)}% of the board)!`);
    } else if (aiScore > totalTiles * 0.5) {
      setWinner("AI");
      setMessage(`Game over! AI wins with ${aiScore} tiles (${Math.round(aiScore / totalTiles * 100)}% of the board)!`);
    } else if (playerScore > aiScore) {
      setWinner("Player");
      setMessage(`Game over! You win with ${playerScore} tiles vs AI's ${aiScore} tiles!`);
    } else if (aiScore > playerScore) {
      setWinner("AI");
      setMessage(`Game over! AI wins with ${aiScore} tiles vs your ${playerScore} tiles!`);
    } else {
      setWinner("Draw");
      setMessage(`Game over! It's a draw! You: ${playerScore} tiles, AI: ${aiScore} tiles`);
    }
  };

  // Handle grid size change
  const handleGridSizeChange = (newWidth, newHeight) => {
    if (newWidth === gridWidth && newHeight === gridHeight) return;
    
    setIsAnimating(true);
    setGridWidth(newWidth);
    setGridHeight(newHeight);
    
    // Generate new grid and ownership with auto‑absorbed blobs
    const {
      grid: newGrid,
      ownership: newOwnership,
      playerScore: newPlayerScore,
      aiScore: newAiScore,
      playerColor: newPlayerColor,
      aiColor: newAiColor,
    } = createInitialGameState(newWidth, newHeight);

    setGrid(newGrid);
    setOwnership(newOwnership);
    
    // Reset game state
    setAiPosition([newHeight - 1, newWidth - 1]);
    setPlayerColor(newPlayerColor);
    setAiColor(newAiColor);
    setPlayerScore(newPlayerScore);
    setAiScore(newAiScore);
    setGameOver(false);
    setWinner(null);
    setMessage("");
    setAvailableColors([]);
    setAnimatingCells([]);
    setIsPlayerTurn(true);
    setCurrentPlayer("Player");
    setPlayerSkipped(false);
    setAiSkipped(false);
    
    // After a brief delay, allow animations again
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  // Handle difficulty change (restart to keep experience consistent)
  const handleDifficultyChange = (newDifficulty) => {
    if (newDifficulty === difficulty) return;
    setDifficulty(newDifficulty);
    restartGame();
  };

  // Animate the absorption of cells
  const animateCellAbsorption = (cellsChanged, newGrid, newOwnership, player, callback) => {
    setIsAnimating(true);
    setCurrentPlayer(player);
    
    // Sort cells by distance from the starting point to create a wave effect
    const startPos = player === "Player" ? playerPosition : aiPosition;
    
    const sortedCells = sortCellsByDistance(cellsChanged, startPos);
    
    // Stagger the animation by animating cells in batches
    const batchSize = Math.ceil(sortedCells.length / 5); // Adjust for speed
    let currentBatch = 0;
    
    const animateBatch = () => {
      const startIdx = currentBatch * batchSize;
      const endIdx = Math.min(startIdx + batchSize, sortedCells.length);
      const batch = sortedCells.slice(startIdx, endIdx);
      
      setAnimatingCells(prev => [...prev, ...batch]);
      
      currentBatch++;
      
      if (currentBatch * batchSize < sortedCells.length) {
        setTimeout(animateBatch, 100); // Adjust timing as needed
      } else {
        // After all cells are animated, complete the move
        setTimeout(() => {
          setGrid(newGrid);
          setOwnership(newOwnership);
          setAnimatingCells([]);
          setIsAnimating(false);
          
          if (callback) {
            callback();
          }
        }, 300); // Give time for the last batch to finish animating
      }
    };
    
    // Start the animation
    animateBatch();
  };

  // Handle player color change
  const handleColorChange = (newColor) => {
    if (gameOver || !isPlayerTurn || newColor === aiColor || isAnimating) {
      return;
    }
    
    // Check if this color is available (will expand territory)
    if (!availableColors.includes(newColor)) {
      return;
    }
    
    const {
      newGrid,
      newOwnership,
      cellsChanged
    } = ownFill(grid, ownership, playerPosition[0], playerPosition[1], playerColor, newColor, "Player", gridWidth, gridHeight);
    
    // Start animation before updating the board
    animateCellAbsorption(cellsChanged, newGrid, newOwnership, "Player", () => {
      // Update player score
      const playerTiles = newOwnership.flat().filter(cell => cell === "Player").length;
      setPlayerScore(playerTiles);
      setPlayerColor(newColor);
      setIsPlayerTurn(false);
      setMessage("");
      setPlayerSkipped(false); // Reset skip flag after a successful move
      
      // Check game over after player's turn
      if (!checkGameOver()) {
        // Check if AI will need to skip its turn
        const aiCanMove = hasAbsorbableCells(newGrid, newOwnership, aiPosition, newColor, "AI", gridWidth, gridHeight);
        if (!aiCanMove) {
          setAiSkipped(true);
          setMessage("AI has no valid moves. Turn skipped.");
          setIsPlayerTurn(true);
          
          // Since AI skipped, check if player also skipped in their previous turn
          if (playerSkipped) {
            endGame(); // End game if both players have skipped
          }
        }
      }
    });
  };
  
  // AI's turn effect
  useEffect(() => {
    if (gameOver || isAnimating) {
      return;
    }
    
    if (!isPlayerTurn) {
      setTimeout(() => {
        // Check if AI can make a move
        const aiCanMove = hasAbsorbableCells(grid, ownership, aiPosition, playerColor, "AI", gridWidth, gridHeight);
        
        if (aiCanMove) {
          setAiSkipped(false); // Reset skip flag after a successful move
          const aiMove = getAiMoveByDifficulty(
            grid,
            ownership,
            aiPosition,
            playerPosition,
            playerColor,
            aiColor,
            difficulty,
            gridWidth,
            gridHeight
          );
          if (aiMove) {
            const {
              newGrid,
              newOwnership,
              cellsChanged
            } = ownFill(grid, ownership, aiPosition[0], aiPosition[1], aiColor, aiMove, "AI", gridWidth, gridHeight);
            
            // Start animation before updating the board
            animateCellAbsorption(cellsChanged, newGrid, newOwnership, "AI", () => {
              // Update AI score
              const aiTiles = newOwnership.flat().filter(cell => cell === "AI").length;
              setAiScore(aiTiles);
              setAiColor(aiMove);
              setIsPlayerTurn(true);
              
              // Check game over after AI's turn
              if (!checkGameOver()) {
                // Check if player will need to skip their turn
                const playerCanMove = hasAbsorbableCells(newGrid, newOwnership, playerPosition, aiMove, "Player", gridWidth, gridHeight);
                if (!playerCanMove) {
                  setPlayerSkipped(true);
                  setMessage("Player has no valid moves. Turn skipped.");
                  setIsPlayerTurn(false);
                  
                  // Since player skipped, check if AI also skipped in its previous turn
                  if (aiSkipped) {
                    endGame(); // End game if both players have skipped
                  }
                }
              }
            });
          }
        } else {
          setAiSkipped(true);
          setMessage("AI has no valid moves. Turn skipped.");
          setIsPlayerTurn(true);
          
          // Check if player also skipped
          if (playerSkipped) {
            endGame(); // End game if both players have skipped
          }
        }
      }, 1000);
    }
  }, [isPlayerTurn, grid, ownership, aiPosition, playerPosition, playerColor, aiColor, isAnimating, gridWidth, gridHeight, difficulty]);

  // Restart the game
  const restartGame = () => {
    const {
      grid: newGrid,
      ownership: newOwnership,
      playerScore: newPlayerScore,
      aiScore: newAiScore,
      playerColor: newPlayerColor,
      aiColor: newAiColor,
    } = createInitialGameState(gridWidth, gridHeight);

    setGrid(newGrid);
    setOwnership(newOwnership);
    setIsPlayerTurn(true);
    setPlayerColor(newPlayerColor);
    setAiColor(newAiColor);
    setPlayerScore(newPlayerScore);
    setAiScore(newAiScore);
    setGameOver(false);
    setWinner(null);
    setMessage("");
    setAnimatingCells([]);
    setIsAnimating(false);
    setCurrentPlayer("Player");
    setPlayerSkipped(false);
    setAiSkipped(false);
  };
  
  // Helper functions for rendering
  const isCellAnimating = (rowIndex, colIndex) => {
    return animatingCells.some(([r, c]) => r === rowIndex && c === colIndex);
  };
  
  const getCellAnimationStyle = (rowIndex, colIndex) => {
    const isAnimating = isCellAnimating(rowIndex, colIndex);
    if (!isAnimating) {
      return {};
    }
    
    return {
      scale: [0.8, 1.1, 1],
      transition: {duration: 0.3}
    };
  };

  return {
    grid,
    gridWidth,
    gridHeight,
    ownership,
    playerPosition,
    aiPosition,
    isPlayerTurn,
    playerColor,
    aiColor,
    playerScore,
    aiScore,
    gameOver,
    winner,
    message,
    availableColors,
    animatingCells,
    isAnimating,
    currentPlayer,
    handleColorChange,
    handleGridSizeChange,
    handleDifficultyChange,
    restartGame,
    isCellAnimating,
    getCellAnimationStyle,
    difficulty
  };
};